import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Banknote, Download, Building2 } from "lucide-react";

const fmtINR = (n) => (n ? "₹" + Number(n).toLocaleString("en-IN") : "₹0");

export default function AdvanceReports() {
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/units").then(({ data }) => {
      setUnits(data || []);
      if (data?.length && !unitId) setUnitId(data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const runReport = async () => {
    if (!unitId) return toast.error("Select unit");
    setLoading(true);
    try {
      const params = new URLSearchParams({ unit_id: unitId, payment_mode: paymentMode });
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const { data } = await api.get(`/advances/reports/by-unit?${params}`);
      setResult(data);
    } catch (e) {
      toast.error("Report failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (unitId) runReport();
    // eslint-disable-next-line
  }, [unitId, paymentMode]);

  const downloadCsv = () => {
    if (!result || !result.rows.length) return;
    const rows = [["Request #", "Voucher #", "Date", "Punch ID", "Employee", "Amount", "Reason"]];
    result.rows.forEach((r) => {
      rows.push([
        r.request_number || "",
        r.voucher_number || "",
        (r.payment_date || "").slice(0, 10),
        r.employee_punch_id || "",
        r.employee_name || "",
        r.amount_approved || 0,
        (r.reason_description || "").replace(/"/g, '""'),
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const unit = units.find((u) => u.id === unitId);
    const fname = `Advances_${(unit?.name || "Unit").replace(/\s/g, "")}_${paymentMode}.csv`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="advance-reports">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Banknote className="h-3 w-3" /> Accounts / Reports
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Advance Disbursement Reports
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">4 reports: Unit × Cash/Bank. Filter by date range.</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Unit</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="w-40 mt-1" data-testid="unit-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Mode</Label>
          <Select value={paymentMode} onValueChange={setPaymentMode}>
            <SelectTrigger className="w-32 mt-1" data-testid="mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Cash">Cash</SelectItem>
              <SelectItem value="Bank">Bank</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">From</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36 mt-1" />
        </div>
        <Button onClick={runReport} variant="outline" size="sm">Run</Button>
        <Button onClick={downloadCsv} disabled={!result?.rows?.length} size="sm" className="brand-bg brand-bg-hover text-white" data-testid="download-btn">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Download CSV
        </Button>
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Count</div>
              <div className="text-2xl font-bold" style={{ fontFamily: "Chivo" }}>{result.count}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700">Total Disbursed</div>
              <div className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "Chivo" }} data-testid="total-amount">{fmtINR(result.total_amount)}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-widest text-blue-700">Mode</div>
              <div className="text-lg font-bold text-blue-700">{result.payment_mode}</div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {result.rows.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-sm">No advances in this filter.</div>
            ) : (
              <table className="w-full text-xs" data-testid="report-table">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2 font-semibold">Voucher</th>
                    <th className="px-3 py-2 font-semibold">Request</th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    <th className="px-3 py-2 font-semibold">Punch</th>
                    <th className="px-3 py-2 font-semibold">Employee</th>
                    <th className="px-3 py-2 font-semibold text-right">Amount</th>
                    <th className="px-3 py-2 font-semibold">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-3 py-1.5">
                        <a href={`/api/advances/${r.id}/voucher-pdf`} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-red-600 hover:underline">
                          {r.voucher_number}
                        </a>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">{r.request_number}</td>
                      <td className="px-3 py-1.5 text-slate-600">{(r.payment_date || "").slice(0, 10)}</td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">{r.employee_punch_id}</td>
                      <td className="px-3 py-1.5 font-semibold">{r.employee_name}</td>
                      <td className="px-3 py-1.5 text-right font-bold font-mono">{fmtINR(r.amount_approved)}</td>
                      <td className="px-3 py-1.5 text-slate-600 text-[10px] italic max-w-[260px] whitespace-normal break-words align-top" title={r.reason_description}>{r.reason_description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
